import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

/**
 * Self-contained delete confirmation dialog - uses refs, NO parent re-renders
 * Requires user to type "delete" to confirm
 */
const DeleteConfirmationDialog: React.FC<{
  dialogRef: React.MutableRefObject<{ open: () => void; close: () => void } | null>;
  selectedRowsRef: React.MutableRefObject<Set<number>>;
  onConfirm: () => void;
}> = ({ dialogRef, selectedRowsRef, onConfirm }) => {
  const [open, setOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);

  // Expose open/close methods via ref (NO parent re-renders)
  useEffect(() => {
    dialogRef.current = {
      open: () => {
        // Update local state instantly (only this component re-renders)
        setSelectedCount(selectedRowsRef.current.size);
        setOpen(true);
      },
      close: () => {
        setOpen(false);
      }
    };
  }, [dialogRef, selectedRowsRef]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    setOpen(false);
    // Call parent handler (runs in background, doesn't block)
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
    >
      <DialogTitle id="delete-dialog-title">
        Confirm Delete
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-dialog-description">
          Are you sure you want to delete {selectedCount} selected record(s)? 
          This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleClose} 
          color="primary"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="error"
          variant="contained"
          autoFocus
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;

